import { SxProps, Theme } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { Dayjs } from 'dayjs';
import { JSX, Ref } from 'react';
import { useIsCompact } from '../../library/useIsCompact';
import { fromPickerDate, PICKER_DATE_FORMAT, toPickerDate } from '../../library/pickerValue';
import {
    pickerDateFieldStyles,
    pickerLabelClearanceStyles,
    pickerPopupStyles,
} from './appPickers.styles';

export interface AppDatePickerProps {
    label: string;
    /** An ISO date (`2026-03-10`). `''` is empty, never today. */
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    name?: string;
    inputRef?: Ref<HTMLInputElement>;
    disabled?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    error?: boolean;
    helperText?: string;
    /** ISO dates too, in the same shape as `value`. */
    minDate?: string;
    maxDate?: string;
    disableFuture?: boolean;
    sx?: SxProps<Theme>;
}

/**
 * A date, in the clinic's colours and the app's language.
 *
 * String in, string out. The `Dayjs` a picker wants exists only between
 * `toPickerDate` and `fromPickerDate` -- form state, every Yup schema and every request
 * body still hold `2026-03-10`, which is why adopting the library changed no schema and
 * no wire format. See `library/pickerValue` for why that boundary matters.
 *
 * Three of the props it sets are load-bearing rather than taste, and all three are silent
 * when wrong:
 *
 *   * **`Desktop`/`Mobile` chosen here, not by MUI.** The plain `DatePicker` decides by
 *     asking `useMediaQuery('@media (pointer: fine)', { defaultMatches: true })`, and
 *     `defaultMatches` only applies when `matchMedia` is *absent*. `setupTests.ts` stubs it
 *     to answer `false` for everything, so `DatePicker` would render the modal-only mobile
 *     variant in every test in the suite. `useIsCompact()` is the app's single definition
 *     of narrow, gives the desktop branch under Jest, and is what `setCompactViewport()`
 *     already flips.
 *   * **`enableAccessibleFieldDOMStructure={false}`.** v8 defaults it on, which swaps the
 *     `<input>` for contenteditable `<span>` sections -- and every `fireEvent.change` and
 *     `toHaveValue` in the suite assumes an input. The cost is section-by-section keyboard
 *     editing; revisit alongside a `user-event` v14 upgrade.
 *   * **An explicit `format`.** Left to the locale, dayjs's `en` is *American* and writes
 *     10 March as `03/10/2026`. A Romanian clinic reading the English UI still writes
 *     `10.03.2026`.
 */
export const AppDatePicker = (props: AppDatePickerProps): JSX.Element => {
    const isCompact: boolean = useIsCompact();

    const shared = {
        label: props.label,
        value: toPickerDate(props.value),
        format: PICKER_DATE_FORMAT,
        disabled: props.disabled,
        disableFuture: props.disableFuture,
        minDate: toPickerDate(props.minDate) ?? undefined,
        maxDate: toPickerDate(props.maxDate) ?? undefined,
        onChange: (next: Dayjs | null): void => props.onChange(fromPickerDate(next)),
        enableAccessibleFieldDOMStructure: false as const,
        slotProps: {
            textField: {
                name: props.name,
                inputRef: props.inputRef,
                onBlur: props.onBlur,
                fullWidth: props.fullWidth,
                required: props.required,
                error: props.error,
                helperText: props.helperText,
                size: 'small' as const,
                slotProps: { inputLabel: { sx: pickerLabelClearanceStyles } },
                sx: props.sx ?? pickerDateFieldStyles,
            },
            desktopPaper: { sx: pickerPopupStyles },
            mobilePaper: { sx: pickerPopupStyles },
        },
    };

    // Branched rather than picking a component into a variable: the two have distinct prop
    // types and TypeScript 4.9 will not unify them.
    return isCompact ? <MobileDatePicker {...shared} /> : <DesktopDatePicker {...shared} />;
};
